import { CommonEvents, Preferences } from '@3ds/common-preferences'
import { requirejsOne } from '@ds/swymkit'
import { BREAKPOINTS } from '@ds/swymkit/utils'
import type { Plugin } from 'vue'
import { createApp } from 'vue'
import Vuekit, { provideKeys } from 'vuekit'

import { register } from '~/contexts/register'
import { pinia } from '~/setup-pinia'

import { setupApplicationStartupInfo } from '~/apps/common-setup'
import App from '~/apps/standalone/App.vue'

import { initGlobalVars } from '~/config/global-var'
import { RootSkeleton } from '~/config/selectors'

import { Navigation404Error, NavigationRedirectError, NavigationToModalError } from '~/router/errors'
import { setupPageAccess } from '~/router/navigation-guard'
import { router } from '~/router/router'

import { getCurrentUser } from '~/entities/user/user/api/users'

import {
  fixSwymConfig,
  getPlatformId,
  getRegistryUrl,
  getSwymUrl,
} from '~/features-shared/config-manager/stores/swym-config'

import type { T_FEATURE_MANAGER_ALLOW_LIST } from '~/features-generic/config-manager/composables/feature-manager'
import {
  initFeatureManager,
  prefixedFeatureManagerKey,
  verifyFeatureAvailability,
} from '~/features-generic/config-manager/composables/feature-manager'
import type { T_PAGE_MANAGER_ALLOW_LIST } from '~/features-generic/config-manager/composables/page-manager'
import { initPageManager } from '~/features-generic/config-manager/composables/page-manager'
import { registerRequireHooks } from '~/features-generic/requirejs/utils/require'

import '~/_index.scss'
import '@ds/vuekit/dist/VUEKIT.css'

/**
 * @description - this function will navigate to publish form in rich client
 */
function navigateToPublishForm() {
  const searchParams = new URLSearchParams(window.location.search)
  const params = Object.fromEntries(searchParams.entries())
  const swymUrl = getSwymUrl()
  const urlParams = new URLSearchParams(params)
  window.location.href = `${swymUrl}/webapps/SwymVuePublishForm/dist/index.html?${urlParams}`
}

// eslint-disable-next-line max-statements, complexity
export async function start(root: Element | string) {
  // checking in url parameters if it contains page=sharefromcatiadev, if yes then navigate to publish form with all the parameters
  const searchParams = new URLSearchParams(window.location.search)

  if (searchParams.get('page') === 'sharefromcatiadev')
    navigateToPublishForm()

  if (import.meta.env.VITE_SWYM_URL) {
    // This is only to init the dev env.
    // For production, this will be done from the index.html

    // Delete the value set in the index.html to be able to point to another env when patching a SB with VITE_SWYM_URL
    // @ts-expect-error dev mode only
    delete window.SWYM_URL
    // @ts-expect-error dev mode only
    delete window.SWYM_TENANT
    // @ts-expect-error dev mode only
    delete window.SWYM_REGISTRY_URL

    const { initSwymConfig } = await import('~/features-shared/config-manager/stores/swym-config')
    await initSwymConfig(import.meta.env.VITE_SWYM_URL)
  }
  // This will fix the config, for example : the registry URL for onPremise
  fixSwymConfig()

  registerRequireHooks()

  // Init global var (eg. window.ds)
  // it should always be done before the call of requirejs
  initGlobalVars()

  // Require IM now so it is preloaded
  requirejsOne<any>('DS/InstantMessaging/InstantMessaging')

  /** The browser icon path that will be used */
  const BROWSER_ICON = '/webapps/UWPClientCode/assets/favicon.ico'
  // Update all link that are related to the icon with the updated path
  document.head.querySelectorAll('link[rel*="icon"]')
    .forEach(el => el.setAttribute('href', `${getSwymUrl()}${BROWSER_ICON}`))

  // HTP1: for native app, it's not needed to re-authenticate with passport
  // hence get the platform information directly from the native part
  // then initialize a first request to obtain csrfToken for later swym requests

  /**
   * initNativeAppConfig() => should always be before any FETCH or initFeatureManager to proxify UWA call (WAFData).
   * initFeatureManager() => should always be before initNativeAppPreferences
   *                         as it can overwrite native app preference.
   * initNativeAppPreferences() => should always be the last when isNativeApp is true.
   */
  if (Preferences.isNativeApp()) {
    const { initNativeAppConfig } = await import('~/features-generic/mobile/utils/init-native-app-preferences')
    // Init forceProxification & WAP event bridge for native application
    await initNativeAppConfig()
  }

  // APP INIT START HERE
  // Async code can be executed here
  // Beware that code execute before app.mount() will delay the first paint
  // Which should not be delayed

  // Get queries from URL
  const query = Object.fromEntries([...new URL(location.href).searchParams.entries()])

  // Add global error handler
  const { handleErrorAppNavigation } = await import('~/features-swym/global-error/utils/global-error')

  // Error listener
  const errorListener = handleErrorAppNavigation(async (payload) => {
    const { mount: mountErrorApp } = await import('~/apps/entrypoint-error')
    unmountMainApp()
    await mountErrorApp({ payload, el: root })
    await nextTick()
    document.querySelector(RootSkeleton.QuerySelector)
      ?.remove()
  })

  errorListener.start()
  await setupApplicationStartupInfo()

  // Explicit default value undefined because if item is not present, it returns null
  const sessionStorageFeatureManager = sessionStorage.getItem(await prefixedFeatureManagerKey()) || undefined

  const featureManager = query?.['feature-manager'] || sessionStorageFeatureManager

  // Init Feature manager before NativeAppConfig => IR-1189697
  await initFeatureManager(featureManager as T_FEATURE_MANAGER_ALLOW_LIST, query)

  if (Preferences.isNativeApp()) {
    const { initNativeAppPreferences } = await import('~/features-generic/mobile/utils/init-native-app-preferences')
    // Init native app preference and toggle off feature disabled on mobile app
    await initNativeAppPreferences()
  }

  verifyFeatureAvailability('app.config.rtc_init')
    .then(async (enabled) => {
      if (!enabled)
        return

      const userId = (await getCurrentUser()).result.login

      const IM = await requirejsOne<any>('DS/InstantMessaging/InstantMessaging')
      IM.init({ platformId: getPlatformId(), registryUrl: getRegistryUrl(), userId })
    })

  // Init page manager
  const pageManager = query?.['page-manager']
  await initPageManager(pageManager as T_PAGE_MANAGER_ALLOW_LIST)

  window.ds = { ...window.ds, swymTenant: getPlatformId() }

  // Create app and add plugin (vue-router, pinia, etc..)
  const isMounted = ref(false)
  const app = createApp(App, { onMounted() { isMounted.value = true } })
  app.use(Vuekit as unknown as Plugin, { globalRegister: true, disableTooltipsOnDevices: true })
  app.use(pinia)

  // eslint-disable-next-line newline-per-chained-call
  app.provide(provideKeys.DropdownMenuOverlayKey, useBreakpoints(BREAKPOINTS).smaller('lg'))

  register(router, isMounted)

  app.use(router)

  // When the router is ready clean useless URL Query
  // It is mandatory to await the router is ready before mounting the app
  // Because all router navigation will not work until the router is not ready
  try {
    await router.isReady()
  }
  catch (error) {
    // navigation guard of context can throw an error, it is handle here at the application start
    if (error instanceof NavigationRedirectError) {
      router.replace(error.to)
    }
    else if (error instanceof Navigation404Error && error.to) {
      router.replace(error.to)
    }
    else {
      router.replace('/home')
      if (error instanceof NavigationToModalError) {
        // Wait for the app to mount and then open the modal
        until(isMounted)
          .toBeTruthy()
          .then(() => {
            useEventBus('swym-app')
              .emit('modal-manager:open', {
                component: (error as NavigationToModalError).modal,
                props: (error as NavigationToModalError).props,
              })
          })
      }
    }
  }

  // removeRouteOnError()

  if (!Preferences.isNativeApp() && router.currentRoute.value.query.ticket) {
    // Hash is a v2 things and only supported for not native app
    await router.replace({
      hash: router.currentRoute.value.hash, // Preserve hash string
      query: {
        ...router.currentRoute.value.query,
        ticket: undefined,
      },
    })
  }

  // Mount the app into the DOM. Starting this point, the app will be rendered
  // The App should be mounted AFTER the router is ready. THIS IS MANDATORY
  app.mount(root)
  nextTick()
    .then(() => {
      document.querySelector(RootSkeleton.QuerySelector)
        ?.remove()
    })
  // Setup router hook
  // Page access should be setup AFTER the app is mounted. THIS IS MANDATORY
  // If the setup is done before the app is mounted, it will break navigation guard for rtconversation
  // Because the topbar will no be instantiated and so the RTC WebSocket will no be connected
  setupPageAccess()

  // Setup service worker
  // setupServiceWorker()

  // Setup real time update and global event handler

  await Promise.allSettled([
    import('~/features-swym/real-time/utils/real-time').then(({ RealtimeUpdatesManager }) => {
      RealtimeUpdatesManager.setup()
    }),
    import('~/features-generic/config-manager/utils/platform-context').then(({ initPlatformContext }) => {
      initPlatformContext()
    }),
    import('~/features-generic/mobile/utils/mobile-event-integration').then(({ initMobileIntegration }) => {
      initMobileIntegration()
    }),
    import('~/features-generic/platform/utils/notification-listeners').then(({ listen3DNotification }) => {
      listen3DNotification(router)
    }),
    import('~/features-generic/tracker/composables/tracker').then(async ({ useTracker }) => {
      const { initPageAnalytics, initTracker } = useTracker('main')
      await initTracker()
      initPageAnalytics()
    }),
    import('~/features-shared/content/utils/log-view-on-content').then(async ({ initStatsTracker }) => {
      initStatsTracker()
    }),
  ])

  import('~/features-generic/mobile/utils/init-native-app-preferences').then(async ({ notifyInitializationEnd }) => {
    notifyInitializationEnd()
  })

  // Init end, emit it to the native app

  import('~/features-shared/content/utils/publish-to-swym').then(module => module.init())

  /**
   * Helper function that will unmount the main app
   */
  function unmountMainApp() {
    try {
      app.unmount()
    }
    catch {}
  }
}

// Handle the case when the assets does not exist anymore on the server (like an upgrade)
window.addEventListener('vite:preloadError', () => {
  // native app case: we emit an event to the native app so it can take action related to SW / Cache clean
  if (Preferences.isNativeApp())
    CommonEvents.emitEventToNativeApp(CommonEvents.EVENTS.ON_VITE_PRELOAD_ERROR, {})

  // Do not reload the page on desktop app
  if (Preferences.isDesktopApp())
    return

  // In any other case, a reload should fetch the new HTML that point to new assets
  window.location.reload()
})
